import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";

// @mui icons
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { usePermissions, useAuth } from "context";
import WorkspaceService from "services/workspace-service";
import UpgradePopup from "layouts/components/Upgrade";
import BrandingPackDialog from "./BrandingPackDialog";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo"; // [NEW]

function Branding() {
    const [brandingPacks, setBrandingPacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingPack, setEditingPack] = useState(null);
    const [error, setError] = useState("");

    // NEW STATE for delete dialog
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [packToDelete, setPackToDelete] = useState(null);

    // [NEW] RBAC State
    const { hasPermission } = usePermissions();
    const { requiredRoles } = useAuth();
    const [showRBAC, setShowRBAC] = useState(false);
    const [rbacFeature, setRbacFeature] = useState("");
    const [rbacRole, setRbacRole] = useState("Manager");

    // [NEW] Demo Mode Restriction
    const [showDemoPopup, setShowDemoPopup] = useState(false);
    const [demoFeature, setDemoFeature] = useState("");

    useEffect(() => {
        fetchBrandingPacks();

        // ✅ Listen for workspace switches (soft refresh)
        const onProfileUpdated = () => fetchBrandingPacks();
        window.addEventListener("profile-updated", onProfileUpdated);
        return () => window.removeEventListener("profile-updated", onProfileUpdated);
    }, []);

    const fetchBrandingPacks = async () => {
        try {
            setLoading(true);
            const response = await WorkspaceService.fetchWorkspaceBrandingPacks();
            setBrandingPacks(response || []);
        } catch (error) {
            console.error("Failed to fetch branding packs", error);
            setError("Failed to load branding packs");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (pack = null) => {
        const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
        if (isDemo) {
            setDemoFeature(pack ? "Editing Branding Packs" : "Adding Branding Packs");
            setShowDemoPopup(true);
            return;
        }

        if (!hasPermission("branding:manage")) {
            setRbacFeature(pack ? "Editing Branding Packs" : "Adding Branding Packs");
            setRbacRole(requiredRoles["branding:manage"] || "Manager");
            setShowRBAC(true);
            return;
        }
        setEditingPack(pack);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingPack(null);
    };

    const handleSaveSuccess = async () => {
        await fetchBrandingPacks();
    };

    const handleDelete = (packId) => {
        const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
        if (isDemo) {
            setDemoFeature("Deleting Branding Packs");
            setShowDemoPopup(true);
            return;
        }

        if (!hasPermission("branding:manage")) {
            setRbacFeature("Deleting Branding Packs");
            setRbacRole(requiredRoles["branding:manage"] || "Manager");
            setShowRBAC(true);
            return;
        }
        setPackToDelete(packId);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (!packToDelete) return;

        try {
            await WorkspaceService.deleteWorkspaceBrandingPack(packToDelete);
            await fetchBrandingPacks();
            setOpenDeleteDialog(false);
            setPackToDelete(null);
        } catch (error) {
            console.error("Failed to delete branding pack", error);
            setError("Failed to delete branding pack");
        }
    };

    const handleSetDefault = async (packId) => {
        const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
        if (isDemo) {
            setDemoFeature("Setting Default Branding Pack");
            setShowDemoPopup(true);
            return;
        }

        if (!hasPermission("branding:manage")) {
            setRbacFeature("Setting Default Branding Pack");
            setRbacRole(requiredRoles["branding:manage"] || "Manager");
            setShowRBAC(true);
            return;
        }
        try {
            await WorkspaceService.setDefaultWorkspaceBrandingPack(packId);
            await fetchBrandingPacks();
        } catch (error) {
            console.error("Failed to set default pack", error);
            setError("Failed to set default pack");
        }
    };

    const renderBrandingPacks = () => {
        if (loading) {
            return (
                <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress color="info" />
                </MDBox>
            );
        }

        if (brandingPacks.length === 0) {
            return (
                <Alert severity="info" sx={{ mt: 2 }}>
                    You haven't created any branding packs yet. Click "Add Branding Pack" to get started.
                </Alert>
            );
        }

        return (
            <Grid container spacing={3} mt={1}>
                {brandingPacks.map((pack) => (
                    <Grid item xs={12} md={6} lg={4} key={pack._id}>
                        <Card
                            sx={{
                                p: 1.5,
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                border: pack.isDefault ? "2px solid #00A1A1" : "1px solid #eee",
                                boxShadow: pack.isDefault ? 3 : 1,
                                transition: "all 0.2s ease-in-out",
                                "&:hover": { transform: "translateY(-2px)", boxShadow: 4 }
                            }}
                        >
                            {/* Header Section */}
                            <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                <MDBox display="flex" alignItems="center" gap={1} flexGrow={1} pr={1}>
                                    <MDTypography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                                        {pack.name}
                                    </MDTypography>
                                    <Tooltip title={`Primary Color: ${pack.primaryColor}`}>
                                        <MDBox
                                            width={14}
                                            height={14}
                                            borderRadius="50%"
                                            sx={{ backgroundColor: pack.primaryColor || "#ccc", border: "1px solid rgba(0,0,0,0.1)", flexShrink: 0 }}
                                        />
                                    </Tooltip>
                                </MDBox>
                                {pack.isDefault && (
                                    <MDBox sx={{
                                        backgroundColor: "#26282b", // Green tick background
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 20,
                                        height: 20,
                                    }}>
                                        <CheckCircleIcon color="accent" sx={{ fontSize: "20px !important" }} />
                                    </MDBox>
                                )}
                            </MDBox>

                            <MDTypography variant="caption" color="text" sx={{ opacity: 0.7, mb: 1.5, display: "block" }}>
                                {pack.companyName || "No Company Specified"}
                            </MDTypography>

                            {/* Multimedia Section (Split view) */}
                            <Grid container spacing={1.5} mb={1.5} flexGrow={1}>
                                <Grid item xs={4}>
                                    <MDBox
                                        sx={{
                                            height: "70px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            border: "1px solid #f0f2f5",
                                            borderRadius: "6px",
                                            bgcolor: "white",
                                            p: 0.5,
                                            overflow: "hidden"
                                        }}
                                    >
                                        {pack.logoUrl ? (
                                            <img
                                                src={pack.logoUrl}
                                                alt="Logo"
                                                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                            />
                                        ) : (
                                            <MDTypography variant="caption" sx={{ fontSize: "0.6rem", opacity: 0.4 }}>NO LOGO</MDTypography>
                                        )}
                                    </MDBox>
                                </Grid>
                                <Grid item xs={8}>
                                    <MDBox
                                        sx={{
                                            height: "70px",
                                            borderRadius: "6px",
                                            overflow: "hidden",
                                            bgcolor: "#f0f2f5",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            border: "1px solid #f0f2f5"
                                        }}
                                    >
                                        {pack.heroUrl ? (
                                            <img
                                                src={pack.heroUrl}
                                                alt="Hero"
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                        ) : (
                                            <MDTypography variant="caption" sx={{ fontSize: "0.6rem", opacity: 0.4 }}>NO HERO</MDTypography>
                                        )}
                                    </MDBox>
                                </Grid>
                            </Grid>

                            {/* Footer Actions */}
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={1.5} borderTop="1px solid #f0f2f5">
                                <MDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>
                                    {pack.created_at ? new Date(pack.created_at).toLocaleDateString() : (pack.createdAt ? new Date(pack.createdAt).toLocaleDateString() : "")}
                                </MDTypography>

                                <MDBox display="flex" gap={0.5}>
                                    {!pack.isDefault && (
                                        <Tooltip title="Set as default pack">
                                            <MDButton
                                                variant="outlined"
                                                color="info"
                                                size="small"
                                                onClick={() => handleSetDefault(pack._id)}
                                                sx={{
                                                    mr: 1,
                                                    borderRadius: "12px",
                                                    px: 2,
                                                    minWidth: 90,
                                                    fontSize: "0.65rem",
                                                    fontWeight: "bold",
                                                    height: "26px",
                                                    borderWidth: "1px !important"
                                                }}
                                            >
                                                Set Default
                                            </MDButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Edit">
                                        <IconButton size="small" onClick={() => handleOpenDialog(pack)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton size="small" onClick={() => handleDelete(pack._id)} color="error">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    };

    return (
        <MDBox>
            <Card id="branding" sx={{ mb: 3, mt: 7, overflow: "visible" }}>
                <MDBox
                    mx={2}
                    mt={-3}
                    py={2}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <MDTypography variant="h6" color="white">
                        White Label Branding
                    </MDTypography>
                    <MDButton
                        variant="contained"
                        color="white"
                        size="small"
                        startIcon={<Icon>add</Icon>}
                        sx={{ color: "info.main" }}
                        onClick={() => handleOpenDialog()}
                    >
                        Add Branding Pack
                    </MDButton>
                </MDBox>

                <MDBox p={2}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                            {error}
                        </Alert>
                    )}

                    {renderBrandingPacks()}
                </MDBox>
            </Card>

            {/* Add/Edit Dialog */}
            <BrandingPackDialog
                open={openDialog}
                onClose={handleCloseDialog}
                onSuccess={handleSaveSuccess}
                initialData={editingPack}
            />

            {/* DELETE CONFIRMATION DIALOG */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2">
                        Are you sure you want to delete this branding pack? This action cannot be undone.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)} color="primary">Cancel</Button>
                    <Button onClick={confirmDelete} color="error">Confirm</Button>
                </DialogActions>
            </Dialog>

            {/* [NEW] RBAC Restriction Popup */}
            <UpgradePopup
                open={showRBAC}
                onClose={() => setShowRBAC(false)}
                type="rbac"
                featureName={rbacFeature}
                roleRequired={rbacRole}
            />

            {/* [NEW] Live Demo Restriction Popup */}
            <LiveDemoRestriction
                open={showDemoPopup}
                onClose={() => setShowDemoPopup(false)}
                featureName={demoFeature}
            />
        </MDBox>
    );
}

export default Branding;
